import React from 'react';
import { PrismButton } from '@prism-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faRedo, faCheck } from '@fortawesome/free-solid-svg-icons';

interface ResumePromptProps {
  lastStep: number;
  stepNames: string[];
  onContinue: (stepIndex: number) => void;
  onStartFresh: () => void;
}

const ResumePrompt: React.FC<ResumePromptProps> = ({ lastStep, stepNames, onContinue, onStartFresh }) => {
  const [selectedStep, setSelectedStep] = React.useState(lastStep);

  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '2rem',
        backgroundColor: 'var(--color-surface-alt)',
        maxWidth: '480px',
        margin: '0 auto 2rem',
      }}
    >
      <h4 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Welcome back!</h4>
      <p style={{ textAlign: 'center', color: 'var(--color-text-faded)', marginBottom: '1.5rem' }}>
        Here's your progress so far:
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem' }}>
        {stepNames.map((name, i) => {
          const completed = i < lastStep;
          const isSelected = i === selectedStep;
          const canClick = i <= lastStep;
          return (
            <li
              key={name}
              onClick={canClick ? () => setSelectedStep(i) : undefined}
              role={canClick ? 'button' : undefined}
              tabIndex={canClick ? 0 : undefined}
              onKeyDown={canClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedStep(i); } } : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                marginBottom: '0.25rem',
                backgroundColor: isSelected ? 'var(--color-primary)' : 'transparent',
                // Fixed white text/border on the saturated primary background.
                color: isSelected ? '#fff' : 'var(--color-text)',
                fontWeight: isSelected ? 600 : 400,
                cursor: canClick ? 'pointer' : 'default',
                opacity: !completed && !isSelected ? 0.5 : 1,
              }}
            >
              {completed && !isSelected ? (
                <span
                  style={{
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-success)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                </span>
              ) : (
                <span
                  style={{
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    border: isSelected ? '2px solid #fff' : '2px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                />
              )}
              <span>{name}</span>
              {isSelected && (
                <span style={{ marginLeft: 'auto', fontSize: '0.8rem', opacity: 0.85 }}>
                  pick up here
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <PrismButton tone="primary-brand" onPrismClick={() => onContinue(selectedStep)}>
          <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: '0.5rem' }} />
          Continue
        </PrismButton>
        <PrismButton tone="secondary" onPrismClick={onStartFresh}>
          <FontAwesomeIcon icon={faRedo} style={{ marginRight: '0.5rem' }} />
          Start Fresh
        </PrismButton>
      </div>
    </div>
  );
};

export default ResumePrompt;
